import {
  Plus,
  User,
  Edit,
  XCircle,
  CheckCircle,
  Search,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Calendar,
} from "lucide-react";
import { useState, useMemo, useContext } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { toast } from "sonner";
import { trpc } from "../trpc";
import MerchantContext from "../contexts/MerchantContext";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Collapse } from "@/components/ui/collapse";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// Helper function to validate phone numbers using the library
const isValidPhone = (phone: string | undefined): boolean => {
  if (!phone) return false;
  // Basic validation - check if it has at least 10 characters (country code + number)
  return phone.length >= 10;
};

// Helper function to clean phone number for backend (remove plus sign and formatting)
const cleanPhoneForBackend = (phone: string | undefined): string => {
  if (!phone) return "";
  return phone.replace(/[\s\-\(\)\+]/g, "");
};

type Employee = {
  id: string;
  name: string;
  phone: string;
  status: "active" | "inactive";
  email?: string;
  crmId?: string;
  projectId?: string;
  calCom?: {
    apiKey?: string;
    eventTypeId?: string;
    teamId?: string;
  };
};

type AssignmentRuleset = {
  id: string;
  name: string;
  assignedEmployees?: string[];
  isFallback: boolean;
  priority: number;
  ruleset: {
    zones?: string[];
    price?: {
      min?: number;
      max?: number;
    };
    department?: "rent" | "sale";
    daysUntilMoveIn?: { min?: number; max?: number };
    projectIds?: string[];
    appointmentType?: "in-person" | "virtual";
    branchName?: string;
    employeeId?: string;
    propertyTypes?: string[];
  };
};

type EmployeesTabProps = {
  employees: Employee[];
  assignmentRulesets: AssignmentRuleset[];
  onCreateEmployee: (
    name: string,
    phone: string,
    email: string,
    crmId?: string,
    projectId?: string
  ) => void;
  onUpdateEmployee: (
    employeeId: string,
    updates: { name: string; phone: string; email?: string; crmId?: string; projectId?: string }
  ) => void;
  onToggleEmployeeStatus: (employeeId: string, status: "active" | "inactive") => void;
  merchantType: "rentals" | "development" | null;
  calComIntegrationEnabled?: boolean;
  restrictEmployeeStatusChanges?: boolean;
  employeeStatusSuperEmails?: string[];
  callerEmail?: string;
};

const STATUS_CHANGE_DENIED_TOOLTIP = "Solo puedes activar/desactivar tu propia cuenta";

export const EmployeesTab = (input: EmployeesTabProps) => {
  const { merchantId } = useContext(MerchantContext);
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    email: "",
    phone: undefined as string | undefined,
    crmId: "",
    projectId: "",
  });
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<string | null>(null);
  const [showEditAdvancedOptions, setShowEditAdvancedOptions] = useState(false);
  const [editValues, setEditValues] = useState({
    name: "",
    email: "",
    phone: undefined as string | undefined,
    crmId: "",
    projectId: "",
  });
  const [phoneErrors, setPhoneErrors] = useState({
    newEmployee: "",
    editingEmployee: "",
  });

  const { data: projects, isLoading: isLoadingProjects } = trpc.crmSelfConfig.listProjects.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId && input.merchantType === "development" }
  );

  const filteredEmployees = useMemo(() => {
    return input.employees.filter((employee) => {
      const matchesSearch =
        employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (employee.email && employee.email.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = statusFilter === "all" || employee.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [input.employees, searchTerm, statusFilter]);

  const getStatusBadgeClasses = (status: "active" | "inactive") => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border-green-200";
      case "inactive":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleAddEmployee = async () => {
    // Validate phone number
    if (!isValidPhone(newEmployee.phone)) {
      setPhoneErrors((prev) => ({
        ...prev,
        newEmployee: "El telefono es requerido",
      }));
      return;
    }

    // Check for duplicate email
    if (newEmployee.email) {
      const emailExists = input.employees.some(
        (emp) => emp.email?.toLowerCase() === newEmployee.email.toLowerCase() && emp.status === "active"
      );

      if (emailExists) {
        toast.error("Email duplicado", {
          description: "Ya existe un empleado activo con este correo electronico",
        });
        return;
      }
    }

    // Clear any previous errors
    setPhoneErrors((prev) => ({ ...prev, newEmployee: "" }));

    try {
      // Clean phone number before sending to backend
      const cleanPhone = cleanPhoneForBackend(newEmployee.phone);
      await input.onCreateEmployee(
        newEmployee.name,
        cleanPhone,
        newEmployee.email,
        newEmployee.crmId || undefined,
        newEmployee.projectId?.trim() || undefined
      );

      toast.success("Empleado creado", {
        description: "El nuevo empleado se agrego correctamente",
      });

      setNewEmployee({ name: "", email: "", phone: undefined, crmId: "", projectId: "" });
      setShowAdvancedOptions(false);
    } catch (error: any) {
      toast.error("Error al crear empleado", {
        description: error?.message || "No se pudo agregar el empleado. Intenta nuevamente.",
      });
    }
  };

  const handleStartEdit = (employee: Employee) => {
    setEditingEmployee(employee.id);

    // Format phone number for PhoneInput component (add + if missing)
    let formattedPhone = employee.phone;
    if (formattedPhone && !formattedPhone.startsWith("+")) {
      formattedPhone = `+${formattedPhone}`;
    }

    setEditValues({
      name: employee.name,
      email: employee.email || "",
      phone: formattedPhone,
      crmId: employee.crmId || "",
      projectId: employee.projectId || "",
    });
    setShowEditAdvancedOptions(false);
  };

  const handleCancelEdit = () => {
    setEditingEmployee(null);
    setEditValues({ name: "", email: "", phone: undefined, crmId: "", projectId: "" });
    setShowEditAdvancedOptions(false);
  };

  const handleSaveEdit = async (employeeId: string) => {
    // Validate phone number
    if (!isValidPhone(editValues.phone)) {
      setPhoneErrors((prev) => ({
        ...prev,
        editingEmployee: "El telefono es requerido",
      }));
      return;
    }

    // Check for duplicate email (excluding the employee being edited)
    if (editValues.email) {
      const emailExists = input.employees.some(
        (emp) =>
          emp.id !== employeeId &&
          emp.email?.toLowerCase() === editValues.email.toLowerCase() &&
          emp.status === "active"
      );

      if (emailExists) {
        toast.error("Email duplicado", {
          description: "Ya existe un empleado activo con este correo electronico",
        });
        return;
      }
    }

    // Clear any previous errors
    setPhoneErrors((prev) => ({ ...prev, editingEmployee: "" }));

    try {
      // Clean phone number before sending to backend
      const cleanPhone = cleanPhoneForBackend(editValues.phone);
      await input.onUpdateEmployee(employeeId, {
        ...editValues,
        phone: cleanPhone,
        crmId: editValues.crmId || undefined,
        projectId: editValues.projectId?.trim() || undefined,
      });

      toast.success("Empleado actualizado", {
        description: "Los cambios se guardaron correctamente",
      });

      setEditingEmployee(null);
      setEditValues({ name: "", email: "", phone: undefined, crmId: "", projectId: "" });
      setShowEditAdvancedOptions(false);
    } catch (error: any) {
      toast.error("Error al actualizar", {
        description: error?.message || "No se pudo guardar los cambios. Intenta nuevamente.",
      });
    }
  };

  const handleEditValueChange = (field: keyof typeof editValues, value: string) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));

    // Clear phone error when user starts typing
    if (field === "phone" && phoneErrors.editingEmployee) {
      setPhoneErrors((prev) => ({ ...prev, editingEmployee: "" }));
    }
  };

  const handleNewEmployeeChange = (field: keyof typeof newEmployee, value: string) => {
    setNewEmployee((prev) => ({ ...prev, [field]: value }));

    // Clear phone error when user starts typing
    if (field === "phone" && phoneErrors.newEmployee) {
      setPhoneErrors((prev) => ({ ...prev, newEmployee: "" }));
    }
  };

  const isOnlyActiveEmployeeInFallbackRule = (employeeId: string): boolean => {
    const fallbackRulesets = input.assignmentRulesets.filter((ruleset) => ruleset.isFallback);

    for (const ruleset of fallbackRulesets) {
      if (ruleset.assignedEmployees?.includes(employeeId)) {
        // Count active employees in this fallback ruleset
        const activeEmployees = ruleset.assignedEmployees.filter((empId) => {
          const employee = input.employees.find((emp) => emp.id === empId);
          return employee && employee.status === "active";
        });

        // If this employee is the only active one in this fallback ruleset
        if (activeEmployees.length === 1 && activeEmployees[0] === employeeId) {
          return true;
        }
      }
    }

    return false;
  };

  const canToggleStatus = (agent: Employee): boolean => {
    if (!input.restrictEmployeeStatusChanges) return true;
    const caller = (input.callerEmail ?? "").toLowerCase();
    if (!caller) return false;
    if ((agent.email ?? "").toLowerCase() === caller) return true;
    return (input.employeeStatusSuperEmails ?? []).includes(caller);
  };

  const handleToggleEmployeeStatus = (employeeId: string, status: "active" | "inactive") => {
    if (status === "inactive" && isOnlyActiveEmployeeInFallbackRule(employeeId)) {
      toast.error("No se puede desactivar", {
        description:
          "Este agente es el unico activo asignado a la regla de ultimo recurso. Debe haber al menos un agente activo en esta regla",
      });
      return;
    }

    const agent = input.employees.find((e) => e.id === employeeId);
    if (agent && !canToggleStatus(agent)) {
      toast.error("No tienes permisos", { description: STATUS_CHANGE_DENIED_TOOLTIP });
      return;
    }

    input.onToggleEmployeeStatus(employeeId, status);
  };

  const renderToggleButton = (agent: Employee, mobile: boolean) => {
    const disabled = !canToggleStatus(agent);
    const baseClass = agent.status === "active"
      ? "border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100"
      : "border-green-200 bg-green-50 text-green-700 hover:border-green-300 hover:bg-green-100";
    const className = cn(baseClass, mobile && "min-h-11");
    const icon = agent.status === "active" ? <XCircle className="size-4" /> : <CheckCircle className="size-4" />;
    const label = agent.status === "active" ? "Desactivar" : "Activar";
    const nextStatus = agent.status === "active" ? "inactive" : "active";
    const button = (
      <Button
        variant="outline"
        size="sm"
        className={className}
        disabled={disabled}
        onClick={() => handleToggleEmployeeStatus(agent.id, nextStatus)}
      >
        {icon}
        {label}
      </Button>
    );
    if (!disabled) return button;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block">{button}</span>
          </TooltipTrigger>
          <TooltipContent>{STATUS_CHANGE_DENIED_TOOLTIP}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div>
      <div className="flex flex-col gap-8">
        {/* Agregar agente section */}
        <div className="rounded-lg border bg-card p-3 md:p-5">
          <div className="flex flex-col gap-4">
            <h4 className="text-lg font-semibold text-gray-900">Agregar agente</h4>
            <div className="flex flex-col gap-4 md:flex-row md:items-end">
              <div className="w-full flex-1 md:w-auto">
                <p className="mb-2 text-sm font-medium text-gray-700">Nombre completo</p>
                <Input
                  placeholder="Ingresa el nombre completo"
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="w-full flex-1 md:w-auto">
                <p className="mb-2 text-sm font-medium text-gray-700">Email</p>
                <Input
                  placeholder="Ingresa el email"
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee((prev) => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div className="w-full flex-1 md:w-auto">
                <p className="mb-2 text-sm font-medium text-gray-700">Telefono</p>
                <PhoneInput
                  placeholder="Ingresa el telefono"
                  value={newEmployee.phone}
                  onChange={(value) => handleNewEmployeeChange("phone", value || "")}
                  defaultCountry="CO"
                  international
                  countryCallingCodeEditable={false}
                  style={{
                    border: phoneErrors.newEmployee ? "1px solid #e53e3e" : "1px solid #e2e8f0",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    fontSize: "14px",
                  }}
                />
                {phoneErrors.newEmployee && (
                  <p className="mt-1 text-xs text-red-500">{phoneErrors.newEmployee}</p>
                )}
              </div>
              <Button
                onClick={handleAddEmployee}
                disabled={
                  !newEmployee.name ||
                  !newEmployee.email ||
                  !newEmployee.phone ||
                  !isValidPhone(newEmployee.phone)
                }
                className="w-full min-h-11 md:w-auto"
              >
                <Plus className="size-4" />
                Agregar
              </Button>
            </div>

            {/* Advanced options toggle */}
            <div>
              <button
                className="flex items-center gap-1 text-sm font-medium text-purple-600 hover:text-purple-700 cursor-pointer"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              >
                {showAdvancedOptions ? (
                  <ChevronUp className="size-4" />
                ) : (
                  <ChevronDown className="size-4" />
                )}
                {showAdvancedOptions
                  ? "Ocultar opciones avanzadas"
                  : "Mostrar opciones avanzadas"}
              </button>
            </div>

            {/* Advanced options section */}
            <Collapse isOpen={showAdvancedOptions}>
              <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-col gap-3">
                  <p className="text-sm font-semibold text-gray-700">Opciones avanzadas</p>
                  <div>
                    <p className="mb-2 text-sm font-medium text-gray-700">CRM ID</p>
                    <Input
                      placeholder="Ingresa el ID del CRM (opcional)"
                      value={newEmployee.crmId}
                      onChange={(e) =>
                        setNewEmployee((prev) => ({ ...prev, crmId: e.target.value }))
                      }
                      className="bg-white"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      ID del empleado en tu sistema CRM externo
                    </p>
                  </div>
                  {input.merchantType === "development" && (
                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-700">Proyecto</p>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                        value={newEmployee.projectId}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, projectId: e.target.value }))
                        }
                      >
                        <option value="">Seleccionar proyecto (opcional)</option>
                        {isLoadingProjects ? (
                          <option disabled>Cargando proyectos...</option>
                        ) : (
                          projects?.map((project) => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))
                        )}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Asignar empleado a un proyecto especifico
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Collapse>
          </div>
        </div>

        {/* Agentes configurados section */}
        <div className="rounded-lg border bg-card p-3 md:p-5">
          <div className="flex flex-col gap-4">
            <h4 className="text-lg font-semibold text-gray-900">
              Agentes configurados ({filteredEmployees.length})
            </h4>

            {/* Filtros y busqueda */}
            <div className="flex flex-col flex-wrap gap-4 md:flex-row md:items-end">
              <div className="flex-1">
                <div className="relative">
                  <Input
                    placeholder="Buscar empleados..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                  <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                </div>
              </div>
              <div className="min-w-[150px] md:min-w-[200px]">
                <p className="mb-2 text-sm font-medium text-gray-700">Filtrar por estado</p>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as "all" | "active" | "inactive")
                  }
                >
                  <option value="all">Todos</option>
                  <option value="active">Activos</option>
                  <option value="inactive">Inactivos</option>
                </select>
              </div>
            </div>

            {/* Lista de agentes */}
            <div className="rounded-lg border">
              <div className="flex flex-col">
                {/* Header de la tabla -- hidden on mobile */}
                {!isMobile && (
                  <div className="flex border-b border-gray-200 bg-gray-50 p-4 text-sm font-semibold text-gray-700">
                    <div className="flex items-center" style={{ flex: "0 0 35%" }}>
                      Agente
                    </div>
                    <div className="flex items-center" style={{ flex: "0 0 12%" }}>
                      Estado
                    </div>
                    <div className="flex max-w-full items-center" style={{ flex: "0 0 20%" }}>
                      Reglas
                    </div>
                    <div className="flex items-center" style={{ flex: "0 0 30%" }}>
                      Activar / Desactivar
                    </div>
                  </div>
                )}

                {/* Filas de agentes */}
                {filteredEmployees.length === 0 ? (
                  <div className="flex min-h-[200px] items-center justify-center p-8">
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-lg text-gray-500">No se encontraron empleados</p>
                      <p className="text-sm text-gray-400">
                        {searchTerm || statusFilter !== "all"
                          ? "Intenta ajustar los filtros de busqueda"
                          : "No hay empleados registrados"}
                      </p>
                    </div>
                  </div>
                ) : (
                  filteredEmployees.map((agent, index) => (
                    <div
                      key={agent.id}
                      className={cn(
                        "transition-all duration-200",
                        index < filteredEmployees.length - 1 && "border-b border-gray-200"
                      )}
                    >
                      {editingEmployee === agent.id ? (
                        // Edit mode - full width layout
                        <div className="bg-gray-50 p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-300">
                              <User className="size-5 text-gray-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex flex-col gap-3">
                                <div className="flex flex-col gap-3">
                                  <div className="flex flex-col gap-2">
                                    <div>
                                      <p className="mb-1 text-xs font-medium text-gray-700">
                                        Nombre completo
                                      </p>
                                      <Input
                                        className="h-8 text-sm"
                                        value={editValues.name}
                                        onChange={(e) =>
                                          handleEditValueChange("name", e.target.value)
                                        }
                                        placeholder="Nombre"
                                      />
                                    </div>
                                    <div>
                                      <p className="mb-1 text-xs font-medium text-gray-700">
                                        Telefono
                                      </p>
                                      <PhoneInput
                                        placeholder="Ingresa el telefono"
                                        value={editValues.phone}
                                        onChange={(value) =>
                                          handleEditValueChange("phone", value || "")
                                        }
                                        defaultCountry="CO"
                                        international
                                        countryCallingCodeEditable={false}
                                        style={{
                                          border: phoneErrors.editingEmployee
                                            ? "1px solid #e53e3e"
                                            : "1px solid #e2e8f0",
                                          borderRadius: "6px",
                                          padding: "6px 10px",
                                          fontSize: "12px",
                                          width: "100%",
                                        }}
                                      />
                                      {phoneErrors.editingEmployee && (
                                        <p className="mt-1 text-xs text-red-500">
                                          {phoneErrors.editingEmployee}
                                        </p>
                                      )}
                                    </div>
                                    <div>
                                      <p className="mb-1 text-xs font-medium text-gray-700">
                                        Email
                                      </p>
                                      <Input
                                        className="h-8 text-sm"
                                        value={editValues.email}
                                        onChange={(e) =>
                                          handleEditValueChange("email", e.target.value)
                                        }
                                        placeholder="Email"
                                        type="email"
                                      />
                                    </div>

                                    {/* Advanced options toggle for edit */}
                                    <div>
                                      <button
                                        className="flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-700 cursor-pointer"
                                        onClick={() =>
                                          setShowEditAdvancedOptions(!showEditAdvancedOptions)
                                        }
                                      >
                                        {showEditAdvancedOptions ? (
                                          <ChevronUp className="size-4" />
                                        ) : (
                                          <ChevronDown className="size-4" />
                                        )}
                                        {showEditAdvancedOptions
                                          ? "Ocultar opciones avanzadas"
                                          : "Mostrar opciones avanzadas"}
                                      </button>
                                    </div>

                                    {/* Advanced options section for edit */}
                                    <Collapse isOpen={showEditAdvancedOptions}>
                                      <div className="flex flex-col gap-2">
                                        <div>
                                          <p className="mb-1 text-xs font-medium text-gray-700">
                                            CRM ID
                                          </p>
                                          <Input
                                            className="h-8 bg-white text-sm"
                                            placeholder="ID del CRM (opcional)"
                                            value={editValues.crmId}
                                            onChange={(e) =>
                                              handleEditValueChange("crmId", e.target.value)
                                            }
                                          />
                                          <p className="mt-1 text-xs text-gray-500">
                                            ID del empleado en tu sistema CRM externo
                                          </p>
                                        </div>
                                        {input.merchantType === "development" && (
                                          <div>
                                            <p className="mb-1 text-xs font-medium text-gray-700">
                                              Proyecto
                                            </p>
                                            <select
                                              className="flex h-8 w-full rounded-md border border-input bg-white px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                              value={editValues.projectId}
                                              onChange={(e) =>
                                                handleEditValueChange(
                                                  "projectId",
                                                  e.target.value
                                                )
                                              }
                                            >
                                              <option value="">Sin proyecto</option>
                                              {isLoadingProjects ? (
                                                <option disabled>Cargando proyectos...</option>
                                              ) : (
                                                projects?.map((project) => (
                                                  <option key={project.id} value={project.id}>
                                                    {project.name}
                                                  </option>
                                                ))
                                              )}
                                            </select>
                                            <p className="mt-1 text-xs text-gray-500">
                                              Asignar empleado a un proyecto especifico
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </Collapse>
                                  </div>
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      size="sm"
                                      className="min-h-11 bg-green-600 text-white hover:bg-green-700"
                                      onClick={() => handleSaveEdit(agent.id)}
                                      disabled={
                                        !editValues.name.trim() ||
                                        !isValidPhone(editValues.phone)
                                      }
                                    >
                                      <Save className="size-4" />
                                      Guardar
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={handleCancelEdit}
                                      className="min-h-11"
                                    >
                                      <X className="size-4" />
                                      Cancelar
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : isMobile ? (
                        // Mobile card layout
                        <div className="p-3 hover:bg-gray-50">
                          <div className="mb-1 flex items-center justify-between">
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-200">
                                <User className="size-4 text-gray-600" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium">{agent.name}</p>
                                <p className="truncate text-xs text-gray-600">{agent.email}</p>
                              </div>
                            </div>
                            <span
                              className={cn(
                                "ml-2 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                                getStatusBadgeClasses(agent.status)
                              )}
                            >
                              {agent.status === "active" ? "Activo" : "Inactivo"}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center justify-between">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleStartEdit(agent)}
                              className="min-h-11 min-w-11 text-blue-600 hover:text-blue-700"
                            >
                              <Edit className="size-3" />
                            </Button>
                            {renderToggleButton(agent, true)}
                          </div>
                        </div>
                      ) : (
                        // Desktop view mode - normal row layout
                        <div className="flex items-center p-4 hover:bg-gray-50">
                          {/* Informacion del agente */}
                          <div
                            className="flex items-center gap-3"
                            style={{ flex: "0 0 35%" }}
                          >
                            <div className="flex size-10 items-center justify-center rounded-full bg-gray-200">
                              <User className="size-5 text-gray-600" />
                            </div>
                            <div className="flex-1">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-gray-900">
                                    {agent.name}
                                  </p>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleStartEdit(agent)}
                                    className="text-blue-600 hover:text-blue-700"
                                  >
                                    <Edit className="size-3" />
                                  </Button>
                                  {input.calComIntegrationEnabled && (
                                    <TooltipProvider delayDuration={200}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span
                                            className={cn(
                                              "flex size-5 items-center justify-center rounded-full",
                                              agent.calCom?.apiKey && agent.calCom?.eventTypeId
                                                ? "bg-green-100"
                                                : "bg-gray-100"
                                            )}
                                          >
                                            <Calendar
                                              className={cn(
                                                "size-3",
                                                agent.calCom?.apiKey && agent.calCom?.eventTypeId
                                                  ? "text-green-600"
                                                  : "text-gray-400"
                                              )}
                                            />
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {agent.calCom?.apiKey && agent.calCom?.eventTypeId
                                            ? "Calendario configurado"
                                            : "Calendario no configurado"}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600">
                                  {agent.phone.startsWith("+")
                                    ? agent.phone
                                    : `+${agent.phone}`}
                                </p>
                                <p className="text-xs text-gray-600">{agent.email}</p>
                                {input.merchantType === "development" && agent.projectId && (
                                  <p className="text-xs font-medium text-purple-600">
                                    Proyecto:{" "}
                                    {projects?.find((p) => p.id === agent.projectId)?.name ||
                                      "Proyecto desconocido"}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Estado */}
                          <div
                            className="flex items-center"
                            style={{ flex: "0 0 12%" }}
                          >
                            <span
                              className={cn(
                                "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                                getStatusBadgeClasses(agent.status)
                              )}
                            >
                              {agent.status === "active" ? "Activo" : "Inactivo"}
                            </span>
                          </div>

                          {/* Reglas */}
                          <div
                            className="flex items-center"
                            style={{ flex: "0 0 20%" }}
                          >
                            <div className="flex max-w-full flex-wrap items-center gap-1">
                              {!input.assignmentRulesets.find((ar) =>
                                ar.assignedEmployees?.includes(agent.id)
                              ) ? (
                                <p className="text-xs text-gray-400">Sin reglas</p>
                              ) : (
                                input.assignmentRulesets
                                  .filter((ar) => ar.assignedEmployees?.includes(agent.id))
                                  ?.map((rule, ruleIndex) => (
                                    <span
                                      key={ruleIndex}
                                      className="inline-flex max-w-full break-words rounded-full border border-blue-200 bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800"
                                    >
                                      {rule.name}
                                    </span>
                                  ))
                              )}
                            </div>
                          </div>

                          {/* Acciones */}
                          <div
                            className="flex items-center gap-2"
                            style={{ flex: "0 0 30%" }}
                          >
                            {renderToggleButton(agent, false)}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
